// The turn (§31 T): what a move spends, what End Turn hands on, and the
// round both are counted in.
//
// **The chess core does not learn about End Turn.** `makeMove` and perft flip
// the side exactly as before; the hold lives here. A move spends one of the
// turn's moves, the first also one tick of the mover's factory (§33 T2), and the clock
// stays with the mover — at nought moves it simply cannot move again — until
// the `pass` command (End Turn) calls `endTurn`.
//
// Imports `reduce.ts` and is imported by the three case modules, never by
// `reduce.ts` itself, so the four-file split keeps its acyclic shape.

import { followClasp } from "./chess/clasp";
import { CLASS_FODDER, type Color, pieceColor, type Side } from "./board";
import { moveFrom, type Move } from "./move";
import { isInCheck } from "./chess/attacks";
import { legalMoves } from "./chess/moveGen";
import { gameStatus, repetitionKey, type GameStatus } from "./chess/status";
import type { GameState } from "./commands";
import {
  areEnemies,
  expireArmour,
  expireClasp,
  expireDisguise,
  expireMoat,
  expireSocks,
  handOn,
  invalidateMoves,
  livingSides,
  pieceAt,
  pieceClassAt,
  pieceIdAt,
  resetTurn,
  takeSkippedTurns
} from "./position";
import {
  controllerOf,
  handClockTo,
  IllegalCommandError,
  idleStuckSides,
  runPickets
} from "./reduce";
import { offTurnFor, offTurnOnceFor, ticksPerTurnFor } from "./campaign/ticks";
import { stokePoints } from "./campaign/stoke";
import { poweredCount } from "./industry/gear";
import { armSpawnClock } from "./campaign/pending";
import { kinglessGrace } from "./campaign/despair";
import { advanceObjectives } from "./campaign/objectives";
import { dealHand } from "./campaign/deck";
import { runGrowth } from "./growth";
import { regenerate } from "./regen";
import { streamFor } from "./random";
import { addTally, tallySide } from "./tally";
import { fireTurrets, refreshTurrets } from "./gunfire";
import { expireFuel, FIEND_FOUNTAIN } from "./industry/state";
import { settleShields } from "./industry/power";
import { boostSources, refreshBoosts } from "./industry/empower";
import { runFlow, runMachines, type TickReport } from "./tick";

const emptyReport = (): TickReport => ({
  mined: 0,
  cooked: 0,
  moved: 0,
  killed: 0,
  minted: [],
  healed: 0,
  cargo: []
});

/**
 * Does Factory Flow bind this side (§31 T4)? **The one place it is asked**:
 * Flow Faster (§31 A13) lifts it while the side powers a Fountain of
 * Fiendishness (Fountain ∧ Fiend's Tail, `poweredCount`).
 */
export function factoryFlowBinds(state: GameState, side: Side): boolean {
  return poweredCount(state.industry, side, FIEND_FOUNTAIN) === 0;
}

/** Why the side on the clock may not move now, or null (§31 T2, T4). */
export function moveRefusal(state: GameState): string | null {
  const pos = state.position;
  if (pos.turnForged > 0 && factoryFlowBinds(state, pos.turn)) {
    return "You forged this turn, so you cannot move until your next turn (Factory Flow).";
  }
  if (pos.movesLeft <= 0 && pos.fodderLeft <= 0) return "No moves left this turn — end your turn.";
  return null;
}

/**
 * Does a move by the piece on `sq` spend a Fodder Move (§33 T1)? A fodder
 * piece's does, unless it is the empowered piece, whose grant was added to
 * the Power pool.
 */
export const spendsFodder = (pos: GameState["position"], sq: number): boolean =>
  pos.empowered?.square !== sq && pieceClassAt(pos, sq) === CLASS_FODDER;

/**
 * Why the piece on `sq` may not move now, or null (§33 T1): the side's
 * gates, then **no piece moves twice in a turn**, then its own pool.
 */
export function pieceMoveRefusal(state: GameState, sq: number): string | null {
  const why = moveRefusal(state);
  if (why) return why;
  const pos = state.position;
  if (pos.empowered?.square === sq) return pos.movesLeft > 0 ? null : "No moves left this turn — end your turn.";
  const id = pieceIdAt(pos, sq);
  if (id > 0 && pos.turnMoved.has(id)) return "That piece has already moved this turn.";
  if (spendsFodder(pos, sq)) {
    return pos.fodderLeft > 0 ? null : "No Fodder Moves left this turn — move a Power piece or end your turn.";
  }
  return pos.movesLeft > 0 ? null : "No Power Moves left this turn — move fodder or end your turn.";
}

/**
 * Why the piece on `sq` may not act for free now, or null (§33 T3): a knight
 * forgoing its move to re-aim spends no pool, but Factory Flow still binds
 * and no piece moves twice in a turn.
 */
export function freeMoveRefusal(state: GameState, sq: number): string | null {
  const pos = state.position;
  if (pos.turnForged > 0 && factoryFlowBinds(state, pos.turn)) {
    return "You forged this turn, so you cannot move until your next turn (Factory Flow).";
  }
  const id = pieceIdAt(pos, sq);
  return id > 0 && pos.turnMoved.has(id) ? "That piece has already moved this turn." : null;
}

/** `openMoves`' memo, valid while the move list and the turn's counters are the ones it read. */
interface OpenMemo {
  legal: readonly Move[];
  key: string;
  moves: Move[];
}
const open = new WeakMap<GameState["position"], OpenMemo>();

/**
 * Every legal move the side on the clock may play now (§33 T1): `legalMoves`
 * less the pieces whose pool is spent or that have moved. What the planner's
 * roots, the naive fallback and the session's lighting read. Memoised on the
 * position and shared — never mutate it.
 */
export function openMoves(state: GameState): Move[] {
  const pos = state.position;
  const legal = legalMoves(pos);
  const key = `${pos.movesLeft}/${pos.fodderLeft}/${pos.turnMoved.size}/${pos.turnForged}/${pos.empowered?.square ?? -1}`;
  const hit = open.get(pos);
  if (hit && hit.legal === legal && hit.key === key) return hit.moves;
  const moves = moveRefusal(state) ? [] : legal.filter((move) => pieceMoveRefusal(state, move.from) === null);
  open.set(pos, { legal, key, moves });
  return moves;
}

/** Why `side` may not forge now, or null (§31 T4). */
export function forgeRefusal(state: GameState, side: Side): string | null {
  if (state.position.turnMoves > 0 && factoryFlowBinds(state, side)) {
    return "You moved this turn — forge before moving, on a turn you do not move (Factory Flow).";
  }
  return null;
}

/**
 * The Empower network an extra move by the piece on `from` would draw on
 * (§33 S1), or undefined: only when its pools refuse it — a pool move is
 * never spent as a boost — and the lowest source when networks overlap.
 */
export function boostSourceFor(state: GameState, from: number): number | undefined {
  if (state.status !== "playing" && state.status !== "check") return undefined;
  if (pieceMoveRefusal(state, from) === null) return undefined;
  const pos = state.position;
  return boostSources(pos, state.industry, pos.turn, from)[0];
}

const playable = new WeakMap<GameState, Move[]>();
/**
 * What a player may play now: `openMoves`, plus each move of a piece an
 * Empower network would carry (§33 S1). Memoised on the snapshot.
 */
export function playableMoves(state: GameState): Move[] {
  const hit = playable.get(state);
  if (hit) return hit;
  const open = movesOpen(state) ? openMoves(state) : [];
  const boostable = new Map<number, boolean>();
  const extra = legalMoves(state.position).filter((move) => {
    const from = moveFrom(move);
    if (open.includes(move)) return false;
    if (!boostable.has(from)) boostable.set(from, boostSourceFor(state, from) !== undefined);
    return boostable.get(from)!;
  });
  const moves = extra.length > 0 ? [...open, ...extra] : open;
  playable.set(state, moves);
  return moves;
}

/**
 * May the side on the clock move? The session's lighting and the planner ask
 * this — a pool left and a piece that may spend it (§33 T1).
 */
export const movesOpen = (state: GameState): boolean => openMoves(state).length > 0;

/**
 * Why a move-spending action no piece takes — a card, a sacrifice, an order
 * from the side — may not happen now, or null. **No piece, a Power Move**
 * (§33 T1): it spends the Power pool, never the fodder's.
 */
export function actionRefusal(state: GameState): string | null {
  return moveRefusal(state) ?? (state.position.movesLeft > 0 ? null : "No Power Moves left this turn — end your turn.");
}

/** Refuse a move-spending command, out loud — of the acting piece when there is one. */
export function requireMove(state: GameState, actor?: number): void {
  const why = actor !== undefined ? pieceMoveRefusal(state, actor) : actionRefusal(state);
  if (why) throw new IllegalCommandError(why);
}

/**
 * One tick of one side's own factory: machines, flow — the
 * per-side body the old every-ply loop ran — then the turn counter.
 *
 * **The counter advances after every tick**, because every once-a-tick rate
 * limit (`minedAt`, `kingActedAt`, ...) compares against it: a second tick at
 * the same count would dig nothing. Nothing per turn reads it — those count
 * `round`.
 */
export function runSideTick(
  next: GameState,
  side: Side,
  report: TickReport,
  /** The last tick before `side`'s next turn starts: no coal is burned (§31 M2). */
  finalTick = false
): void {
  const { position, industry } = next;
  const machines = runMachines(position, industry, side, finalTick);
  if (machines.burned) report.burned = (report.burned ?? 0) + machines.burned;
  if (machines.cookedMetal) report.cookedMetal = (report.cookedMetal ?? 0) + machines.cookedMetal;
  report.healed = (report.healed ?? 0) + (machines.healed ?? 0);
  report.mined += machines.mined;
  if (side === tallySide(next.run) && machines.mined > 0) {
    next.tally = addTally(next.tally, { mined: machines.mined });
  }
  report.cooked += machines.cooked;
  report.minted.push(...machines.minted);
  const flow = runFlow(position, industry, side);
  report.moved += flow.moved;
  report.cargo!.push(...flow.cargo, ...(machines.cargo ?? []));
  industry.turnCounter++;
}

/** Status mid-turn: the holder cannot be stalemated by a hold, only checked. */
function holdStatus(next: GameState, mover: Side): GameStatus {
  const pos = next.position;
  // A royal lost mid-turn is still a loss, `gameStatus`' first rule.
  if (
    pos.kings[mover] < 0 &&
    pos.kings.some((sq, side) => sq >= 0 && side !== mover) &&
    !(kinglessGrace(next.run) && pos.counts[mover] > 0)
  ) {
    return "checkmate";
  }
  return isInCheck(pos, mover) ? "check" : "playing";
}

/** What every action that is not the handoff settles: expiries, objectives, the spawn clock, status. */
function settle(state: GameState, next: GameState, mover: Side): void {
  const position = next.position;
  expireArmour(position);
  expireMoat(position);
  expireSocks(position);
  // The clamp lasts the turn (§31 C8): carried, and dropped at End Turn.
  followClasp(state.position, position);
  expireDisguise(position);
  if (next.run?.level) next.run = { ...next.run, objectives: advanceObjectives(next) };
  armSpawnClock(next);
  next.status = holdStatus(next, mover);
}

/**
 * A free action landed (forge, build, upgrade, place): no move, no tick, no
 * handoff — only the status and the objectives it may have changed.
 */
export function settleAction(next: GameState, mover: Side): void {
  if (next.run?.level) next.run = { ...next.run, objectives: advanceObjectives(next) };
  next.status = holdStatus(next, mover);
}

/**
 * A move-spending action has been applied to `next`: spend the move and run
 * the one tick that follows it (§31 T2, T3). The clock stays.
 *
 * `made` is true when `makeMove` already advanced the ply (`applyMove`); every
 * other move-like action (re-aim, drill, lance, ...) moves no piece through
 * the core and pays its ply here.
 *
 * **Only the turn's first move ticks** (§33 T2, the V2 doc): a later move
 * ticks nothing, and End Turn runs what the turn still owes.
 */
export function spendMove(
  state: GameState,
  next: GameState,
  mover: Color,
  irreversible: boolean,
  made = false,
  /** The acting piece's square before the action: its pool and its once-a-turn mark (§33 T1). */
  actor?: number,
  /**
   * Spends no pool, the caller having checked its own gate: `"knight"` a
   * re-aim (§33 T3), `"boost"` an Empower network's extra move (§33 S1),
   * which also leaves `turnMoves` alone so forging stays open (T4).
   */
  free: false | "knight" | "boost" = false
): void {
  if (!free) requireMove(state, actor);
  const pos = next.position;
  if (!made) {
    invalidateMoves(pos);
    pos.ply++;
  }
  if (free) {
    // No pool: the knight's own move is the price.
  } else if (actor !== undefined && spendsFodder(state.position, actor)) {
    pos.fodderLeft = Math.max(0, pos.fodderLeft - 1);
  } else pos.movesLeft = Math.max(0, pos.movesLeft - 1);
  const id = actor === undefined ? 0 : pieceIdAt(state.position, actor);
  if (id > 0 && state.position.empowered?.square !== actor) pos.turnMoved.add(id);
  // The turn's first action of any kind ticks once (§33 T2).
  const first = pos.turnTicks === 0;
  if (free !== "boost") pos.turnMoves++;
  const report = emptyReport();
  if (first) {
    runSideTick(next, mover, report);
    pos.turnTicks++;
  }
  next.lastTick = report;
  next.lastGambit = undefined;
  // A capture or a pawn move can never be repeated past, so the count starts
  // again now; the handoff adds the key.
  if (irreversible) next.repetitions = new Map();
  settle(state, next, mover);
}

/**
 * End Turn (§31 T2): the turn's remaining ticks, the clock handed on, the
 * round counted, and everything that happens when control changes.
 *
 * The `pass` command and the Full Production Gambit both end a turn here — one
 * body, because every copy is a place a multi-move turn is left on the clock.
 */
export function endTurn(state: GameState, next: GameState, mover: Color): void {
  const { position, industry } = next;
  const report = emptyReport();

  // **The ticks the turn still owes: `max(1, ticksPerTurn − ticks run)`.** A
  // one-move turn is move→tick, End Turn→tick; a forging turn gets them all here.
  const due = Math.max(1, ticksPerTurnFor(next.run, industry, mover) - position.turnTicks);
  for (let i = 0; i < due; i++) runSideTick(next, mover, report, i === due - 1);
  // Deflector Shields fed this turn are lit for the round (§31 A9).
  settleShields(industry, mover);
  // **The volley** (§31 S8): the mover's turrets fire, fed by this turn's
  // ticks; what they leave is overwatch until their next turn starts.
  const volley = fireTurrets(position, industry, next.seed, mover);

  // A turn that moved has paid its plies; one that did not pays one here, so
  // every turn costs at least a ply. Timers no longer read it — they count
  // `round` (expiries, spawn, clear-front, sacrifice, despair's slot).
  if (position.turnMoves === 0) {
    position.ply++;
    position.halfmove++;
  }

  // The clock, and the round: a hop to an index not above the one it left is
  // the cycle wrapping to the lowest living side (§31 T1). Counted per hop, so
  // a skipped turn (§26 F4) that wraps is counted too.
  let wraps = 0;
  const hop = (from: Side, to: Side): void => {
    if (to <= from) wraps++;
  };
  handOn(position, mover);
  hop(mover, position.turn);
  // A side that is missing this turn hands it straight on — here and never in
  // `passTurn`, which a search reaches (`takeSkippedTurns` has the argument).
  takeSkippedTurns(position, (board) => legalMoves(board).length > 0, hop);
  handClockTo(position, position.turn, industry);
  // A Confessor's grant lapses with the turn, unspent moves included.
  position.empowered = null;

  // **Off-Turn Production at each rollover** (§31 T3): N points run N ticks
  // per enemy faction alive last round. Before the new holder's turn fields
  // are read, so these ticks are nobody's turn's.
  position.roundSides |= livingMask(next);
  for (let i = 0; i < wraps; i++) {
    // Regeneration closes the round (§31 K5), before it is counted on.
    regenerate(position, position.round);
    position.round++;
    for (const side of livingSides(position)) {
      // Standing points plus the one-shot ones (§31 C4), spent at this rollover.
      // Stoke the Forges (§31 A10) adds its point off the board.
      const points =
        offTurnFor(next.run, side) + offTurnOnceFor(next.run, side) + stokePoints(industry, side);
      if (points <= 0) continue;
      const ticks = points * enemiesLastRound(next, side);
      for (let t = 0; t < ticks; t++) runSideTick(next, side, report, t === ticks - 1);
    }
    if (next.run?.offTurnOnce) next.run = { ...next.run, offTurnOnce: undefined };
    position.roundSides = livingMask(next);
  }
  resetTurn(position);
  // **Fuel points vanish at the start of their owner's turn** (§31 M2).
  expireFuel(industry, (sq) => pieceColor(pieceAt(position, sq)), position.turn);
  // A turret's round starts with its owner's turn (§31 S8).
  refreshTurrets(industry, position.turn);
  // An Empower network's grants too (§33 S1).
  refreshBoosts(industry, position.turn);

  // Unicorn pickets fire on the handoff — control changing hands, never a search.
  const shots = volley;
  if (controllerOf(next, mover) !== controllerOf(next, position.turn)) shots.push(...runPickets(next, mover));
  if (shots.length > 0) {
    report.shots = shots;
    report.killed = shots.filter((shot) => shot.killed).length;
  }
  next.lastTick = report;
  next.lastGambit = undefined;

  expireArmour(position);
  expireMoat(position);
  expireSocks(position);
  expireClasp(position);
  expireDisguise(position);
  if (next.run?.level) next.run = { ...next.run, objectives: advanceObjectives(next) };

  // Ground coming back takes its turn when the clock comes back round.
  if (industry.growth.size > 0 && position.turn === (next.run?.side ?? position.turn)) {
    runGrowth(position, industry, (sq) => streamFor(next.seed, position.ply, sq));
  }
  // A fresh hand at the moment the clock comes back to the run's side.
  if (next.run && position.turn === next.run.side) {
    next.run = { ...next.run, hand: dealHand(next.run, next.seed, industry.turnCounter) };
  }
  armSpawnClock(next);

  // Before the repetition key, so the key names whoever is actually to move.
  idleStuckSides(position, industry);
  const repetitions = new Map(next.repetitions);
  const key = repetitionKey(position);
  repetitions.set(key, (repetitions.get(key) ?? 0) + 1);
  next.repetitions = repetitions;
  next.status = gameStatus(
    position,
    legalMoves(position).length,
    repetitions,
    !next.run,
    kinglessGrace(next.run)
  );
}

function livingMask(state: GameState): number {
  let mask = 0;
  for (const side of livingSides(state.position)) mask |= 1 << side;
  return mask;
}

/** Enemy factions of `side` alive at any handoff of the round just ended. */
function enemiesLastRound(state: GameState, side: Side): number {
  const pos = state.position;
  let count = 0;
  for (let other = 0; other < pos.sides; other++) {
    if ((pos.roundSides & (1 << other)) === 0) continue;
    if (areEnemies(pos, side, other)) count++;
  }
  return count;
}
